import {Component, ElementRef, input, output, signal, viewChild} from '@angular/core';
import {DocumentSummary} from '../../../api';
import {DocumentItem} from '../../features/documents/components/document-item/document-item';
import {ElciaButton} from '../buttons/button/button';
import {TranslatePipe} from '@ngx-translate/core';
import {Label} from '../label/label';

@Component({
  selector: 'elcia-document-uploader',
  imports: [DocumentItem, ElciaButton, TranslatePipe, Label],
  templateUrl: './document-uploader.html',
})
export class DocumentUploader {
  label = input<string>('entity.documents');
  hint = input<string | null>(null);
  documents = input<DocumentSummary[]>([]);
  pendingFiles = input<File[]>([]);
  onFilesSelected = output<File[]>();
  onDelete = output<DocumentSummary>();
  onDownload = output<DocumentSummary>();
  onRemovePendingFile = output<number>();

  protected isDragOver = signal(false);

  private fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  protected openFilePicker() {
    this.fileInput()?.nativeElement.click();
  }

  protected onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  protected onDragLeave(event: DragEvent) {
    // Keep the highlight while moving between child elements (document/pending rows);
    // only clear it when the pointer actually leaves the drop zone.
    const related = event.relatedTarget;
    const current = event.currentTarget as Node;
    if (related instanceof Node && current.contains(related)) return;
    this.isDragOver.set(false);
  }

  protected onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) this.onFilesSelected.emit(files);
  }

  protected handleFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length > 0) this.onFilesSelected.emit(files);
    input.value = '';
  }

  protected getFileIcon(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pi pi-file-pdf text-red-400';
    if (['doc', 'docx'].includes(ext ?? '')) return 'pi pi-file-word text-blue-400';
    if (['xls', 'xlsx'].includes(ext ?? '')) return 'pi pi-file-excel text-green-400';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext ?? '')) return 'pi pi-image text-purple-400';
    return 'pi pi-file text-surface-400';
  }
}
